/**
 * 이상한 방 - RoomState 불변 조작 레이어
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 2절 / 3.1절
 *
 * 이 파일의 모든 함수는 **새 RoomState를 반환**한다. 제자리 변경은 한 곳도 없다.
 * 이유는 취향이 아니라 판정 정확성이다 - 서버는 라운드당 `original`(외우기 종료 스냅샷)과
 * `mutated`(체인저 작업 결과) 두 상태를 동시에 들고 diff를 낸다(2절).
 * 한쪽을 제자리 변경하면 스냅샷이 오염돼 diff 자체가 거짓이 된다.
 *
 * 규칙 위반과 프로그래밍 오류를 구분한다:
 *  - **게임 규칙 거부**(경계 초과, 셀 점유 충돌, 무변화 회전 등)는 이 파일이 다루지 않는다.
 *    `verbs.ts`가 `VerbResult`로 돌려준다. UI는 그 결과로 동사를 비활성화한다(4.3절).
 *  - **계약 위반**(중복 id 발급, 없는 인스턴스 수정, 카탈로그에 없는 catalogId)은 여기서 throw 한다.
 *    이런 상태는 조용히 넘기면 diff가 틀리는데도 게임이 계속 굴러가므로 즉시 터뜨리는 쪽이 싸다.
 */

import type {
  Cell3,
  CatalogEntry,
  CatalogId,
  InstanceId,
  ObjectInstance,
  PaletteIndex,
  PlayerId,
  RoomState,
  Rot,
} from './types'
import { cellKey, occupiedCells } from './grid'

/** 카탈로그 조회 테이블. 소품 규격(8절)은 `CatalogEntry`가, 조회 책임은 이 맵이 진다. */
export type Catalog = ReadonlyMap<CatalogId, CatalogEntry>

/**
 * 오브젝트에 적용 가능한 변경 필드.
 * `id`·`catalogId`·`placedBy`가 빠진 것은 실수가 아니다 - 원칙 1(상태 필드는 변화 동사에서 역산한다)에 따라
 * 이 셋을 바꾸는 동사가 존재하지 않는다. id 재사용은 2절이 명시적으로 금지한다.
 */
export type ObjectPatch = Readonly<{
  cell?: Cell3
  rot?: Rot
  colorId?: PaletteIndex
}>

/** 빈 방(템플릿 셸)을 만든다. 꾸미기 페이즈의 시작 상태다. */
export function createRoom(templateId: string): RoomState {
  return { templateId, objects: new Map() }
}

/**
 * 오브젝트를 추가한 새 상태를 반환한다.
 * 중복 id는 throw - 2절이 id 재사용을 금지하는 이유는 재사용 시 제거/추가 diff가 뒤섞이기 때문이다.
 */
export function addObject(state: RoomState, obj: ObjectInstance): RoomState {
  if (state.objects.has(obj.id)) {
    throw new Error(`addObject: id 재사용 금지 - 이미 존재하는 InstanceId '${obj.id}'`)
  }

  const objects = new Map(state.objects)
  objects.set(obj.id, obj)
  return { ...state, objects }
}

/** 오브젝트를 제거한 새 상태를 반환한다. 없는 id는 호출자의 버그이므로 throw 한다. */
export function removeObject(state: RoomState, id: InstanceId): RoomState {
  if (!state.objects.has(id)) {
    throw new Error(`removeObject: 존재하지 않는 InstanceId '${id}'`)
  }

  const objects = new Map(state.objects)
  objects.delete(id)
  return { ...state, objects }
}

/**
 * 오브젝트의 일부 필드만 바꾼 새 상태를 반환한다.
 * 원본 상태·원본 오브젝트 모두 그대로 남는다(스냅샷 비교의 전제).
 */
export function updateObject(state: RoomState, id: InstanceId, patch: ObjectPatch): RoomState {
  const current = state.objects.get(id)
  if (current === undefined) {
    throw new Error(`updateObject: 존재하지 않는 InstanceId '${id}'`)
  }

  const objects = new Map(state.objects)
  objects.set(id, { ...current, ...patch })
  return { ...state, objects }
}

export function getObject(state: RoomState, id: InstanceId): ObjectInstance | undefined {
  return state.objects.get(id)
}

/** 반복·렌더링용 스냅샷 배열. 반환 배열을 바꿔도 방 상태에는 영향이 없다. */
export function listObjects(state: RoomState): readonly ObjectInstance[] {
  return [...state.objects.values()]
}

/**
 * 셀 점유 맵을 만든다. `cellKey` → 그 셀을 점유한 오브젝트의 id.
 *
 * 3.1절의 "한 셀에는 오브젝트 하나"를 검증 가능한 형태로 편 것이다.
 * 이 한 규칙이 두 익스플로잇(책상 메시 안에 숨기는 완전 은닉 / 방 밖 배출)을 동시에 닫는다.
 *
 * 이미 규칙을 어긴 상태가 들어오면 throw 한다. 충돌을 조용히 덮으면
 * "화면에는 있는데 점유 맵에는 없는 오브젝트"가 생겨 이후 배치 검증이 전부 거짓이 된다.
 */
export function buildOccupancy(state: RoomState, catalog: Catalog): ReadonlyMap<string, InstanceId> {
  const occupancy = new Map<string, InstanceId>()

  for (const obj of state.objects.values()) {
    const entry = catalog.get(obj.catalogId)
    if (entry === undefined) {
      throw new Error(
        `buildOccupancy: 카탈로그에 없는 catalogId '${obj.catalogId}' (인스턴스 '${obj.id}')`,
      )
    }

    for (const cell of occupiedCells(obj.cell, entry, obj.rot)) {
      const key = cellKey(cell)
      const holder = occupancy.get(key)
      if (holder !== undefined) {
        throw new Error(
          `buildOccupancy: 셀 점유 규칙 위반 - 셀 ${key}를 '${holder}'와 '${obj.id}'가 동시에 점유`,
        )
      }
      occupancy.set(key, obj.id)
    }
  }

  return occupancy
}

/**
 * 해당 플레이어가 배치한 오브젝트 수.
 * 꾸미기 페이즈의 인당 배치 수 제한(`RoomConfig.placementsPerPlayer`) 판정에 쓴다.
 */
export function countByPlayer(state: RoomState, playerId: PlayerId): number {
  let count = 0
  for (const obj of state.objects.values()) {
    if (obj.placedBy === playerId) count += 1
  }
  return count
}

/**
 * 두 스냅샷의 차집합으로 복원한 '제거' 변화. `render/hitProxy`의 `RemovalChangeLike`와 같은 모양이다.
 *
 * `original`에 있고 `mutated`에 없는 id가 곧 제거다(2절: 스냅샷 두 벌이 판정의 진실이고
 * 조작 로그는 보조 데이터다). 순서는 `original`의 Map 순서다 - 프록시 세우기에 순서는 무의미하지만
 * 같은 입력이면 같은 배열이어야 테스트가 배열 비교로 끝난다.
 */
export type RemovedChange = Readonly<{ verb: 'remove'; targetId: InstanceId }>

/**
 * 원본에는 있는데 변경본에는 없는 오브젝트를 '제거' 변화 목록으로 돌려준다.
 *
 * **찾는 사람의 클라이언트가 `changes` 없이 제거 프록시를 세우는 유일한 방법이다.**
 * 빈 자리는 광선이 통과하므로 제거된 곳에는 보이지 않는 표적을 세워야 조준이 가능한데
 * (`render/hitProxy.ts`, 상태모델 6.1절), 그 함수는 제거 변화의 `targetId`를 요구한다.
 * 멀티에서 정답표(`changes`)는 수색 중 찾는 사람에게 가지 않으므로, 클라이언트가 이미 갖고 있는
 * 두 방(11.2절 - 외우기에 `original`, 수색에 `mutated`)의 차집합으로 같은 목록을 만든다.
 * 새로 새는 정보는 없다: 두 방을 가진 클라이언트는 어차피 이 차집합을 만들 수 있다.
 *
 * `generateChanges`가 만든 제거 변화의 `targetId` 집합과 정확히 같아야 한다(`game/changeGen.test.ts`가 실물 생성기로 지킨다).
 * 이동은 id가 보존되므로 여기에 잡히지 않고, 추가는 `mutated`에만 있으므로 잡히지 않는다.
 */
export function removedBetween(original: RoomState, mutated: RoomState): readonly RemovedChange[] {
  const removed: RemovedChange[] = []
  for (const id of original.objects.keys()) {
    if (!mutated.objects.has(id)) removed.push({ verb: 'remove', targetId: id })
  }
  return removed
}

/** 두 스냅샷의 대조로 복원한 '이동' 변화. `removedBetween`과 같은 자리, 같은 모양이다. */
export type MovedChange = Readonly<{ verb: 'move'; targetId: InstanceId }>

/**
 * 원본과 변경본에 **둘 다 있는데 셀이 다른** 오브젝트를 '이동' 변화 목록으로 돌려준다.
 *
 * 옮겨진 물건은 **옮기기 전 자리도 정답**이다(6.3절 - `render/hitProxy.ts` 머리말).
 * 그 빈 자리에도 보이지 않는 표적을 세워야 하므로 제거와 같은 재료가 필요하고, 찾는 사람은 정답표를 갖고 있지 않으므로
 * 여기서도 두 방의 대조로 만든다. `removedBetween`과 같은 근거다 - 두 방을 가진 클라이언트는 어차피 이 대조를 할 수 있다.
 *
 * 회전·색만 바뀐 것은 셀이 같으므로 잡히지 않는다. 이동의 정답 자리가 둘이 되는 것과 달리, 그 둘은 물건이 그 자리에 그대로 있다.
 */
export function movedBetween(original: RoomState, mutated: RoomState): readonly MovedChange[] {
  const moved: MovedChange[] = []
  for (const [id, before] of original.objects) {
    const after = mutated.objects.get(id)
    if (after === undefined) continue
    if (cellKey(before.cell) !== cellKey(after.cell)) moved.push({ verb: 'move', targetId: id })
  }
  return moved
}

/**
 * 빈 자리에 표적이 필요한 변화 전부 - 제거된 자리와 **옮기기 전 자리**다.
 *
 * 두 목록을 부르는 자리마다 이어 붙이면 "무엇에 프록시가 필요한가"가 호출부 수만큼 생긴다.
 * 실제로 그 목록이 갈리면 한 화면에서만 이동의 옛 자리가 조준 불가능해지고, 그건 사람이 찾기 가장 어려운 종류의 차이다.
 */
export function proxiedBetween(
  original: RoomState,
  mutated: RoomState,
): readonly (RemovedChange | MovedChange)[] {
  return [...removedBetween(original, mutated), ...movedBetween(original, mutated)]
}
